import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  useToast,
  SimpleGrid,
  Heading,
  Stack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  useColorMode,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  Switch,
  IconButton,
  Radio,
  Link,
  RadioGroup,
  Select,
} from "@chakra-ui/react";
import { AddIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import EditableText from "../EditableText/EditableText";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisH } from "@fortawesome/free-solid-svg-icons";
import { cqAPI, getBid } from "../../lib/Auth";
import moment from "moment";

function Templates() {
  const [templates, setTemplates] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState(null); // State for current template being updated
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sendOption, setSendOption] = useState("12");
  const [excludeSigned, setExcludeSigned] = useState(false);
  const fileInputRef = useRef(null);
  const toast = useToast();
  const { colorMode } = useColorMode();
  const bid = getBid();

  const fetchTemplates = async () => {
    try {
      const response = await cqAPI.get("/settings/esign");
      setTemplates(response.data); // Assuming response.data.data is the correct path
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const downloadFile = async (attachmentUrl, fileName) => {
    try {
      const downloadUrl = await cqAPI.downloadFile(attachmentUrl);
      if (!downloadUrl) {
        console.error("File unavailable for download:", attachmentUrl);
        return;
      }

      // Create a temporary link element and trigger the download
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", fileName + '.pdf'); // Set the file name for the download
      document.body.appendChild(link);
      link.click();

      // Clean up by revoking the blob URL and removing the temporary link
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download file:", error);
    }
  };

  const handleFileButtonClick = (template = null) => {
    setCurrentTemplate(template); // Set the current template
    fileInputRef.current.click(); // Trigger file input click on button click
  };

  const uploadTemplate = async (event) => {
    const files = event.target.files;
    const uploadedFiles = [];
    for (let file of files) {
      if (file.type !== "application/pdf") {
        toast({
          title: "Invalid file type.",
          description: "Only PDF files are allowed.",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        continue;
      }
      const formData = new FormData();
      formData.append("file", file);
      try {
        const ep = currentTemplate
          ? `/settings/esign/upload-template?template_id=${currentTemplate._id}`
          : "/settings/esign/upload-template";
        const response = await cqAPI.post(ep, formData);
        if (response.type === "ok") {
          uploadedFiles.push({
            name: file.name || "Form",
            url: response.url,
            mimetype: file.type,
          });
        } else {
          toast({
            title: "Error uploading file.",
            description: response.msg,
            status: "error",
            duration: 9000,
            isClosable: true,
          });
          break;
        }
      } catch (error) {
        toast({
          title: "Error uploading file.",
          description: error.message,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        break;
      }
    }
    if (uploadedFiles.length > 0) {
      toast({
        title: "File(s) uploaded successfully.",
        status: "success",
        duration: 9000,
        isClosable: true,
      });
      await fetchTemplates();
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const updateTemplateField = async (template, field, value) => {
    const response = await cqAPI.post("/settings/esign", {
      template_id: template._id,
      field,
      value,
    });
    if (response.type === "error") {
      if (response.msg) {
        toast({
          title: "Error updating the template.",
          description: response.msg,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
      return false;
    }
    await fetchTemplates();
    return true;
  };

  const handleSend = async () => {
    try {
      const response = await cqAPI.post("/forms/template/batchsend", {
        template_id: currentTemplate._id,
        ts: Date.now() - parseInt(sendOption) * 30 * 24 * 3600000,
        exclude : excludeSigned,
      });
      if (response.type === "ok") {
        toast({
          title: "Signature requests sent successfully.",
          status: "success",
          duration: 9000,
          isClosable: true,
        });
      } else if (response.msg){
        toast({
          title: "Error sending signature requests.",
          description: response.msg,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error sending signature requests.",
        description: error.message,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
    setIsModalOpen(false);
  };

  return (
    <Box
      p={4}
      overflowY="auto"
      maxHeight="100vh"
      width="100%"
      position="relative"
    >
      <Flex
        p={4}
        overflowY="auto"
        maxHeight="100vh"
        padding="8px 24px 24px 24px"
        height="95vh"
        bgColor={colorMode === "light" ? "#FFFFFF" : "#212330"}
        borderRadius="16px"
        flexDirection="column"
      >
        <Flex justifyContent="flex-end" mb={4}>
          <Input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={uploadTemplate}
            hidden
          />
          <Button
            onClick={() => handleFileButtonClick()}
            leftIcon={<AddIcon />}
          >
            Add Template
          </Button>
        </Flex>
        {templates.map((template, index) => (
          <Box
            className="template"
            key={template._id}
            mb="2"
            padding="16px 24px 16px 24px"
            bgColor={colorMode === "light" ? "gray.100" : "gray.700"}
            borderRadius="md"
          >
            <Flex flexDirection="row" alignItems="center">
              <Flex width="250px" align="center">
                <EditableText
                  onSave={(newValue) =>
                    updateTemplateField(template, "name", newValue)
                  }
                  initialValue={template.name}
                  placeholder="Name"
                  editable={template.bid === bid}
                  pattern={/[^\ ]{3,}/}
                  invalid="more than 3 character."
                />
              </Flex>
              <Flex width="150px" align="center" justify="flex-end" pr="20px">
                {template.bid === bid ? (
                  <Switch
                    isChecked={template.shared}
                    onChange={(e) => {
                      updateTemplateField(template, "shared", e.target.checked);
                    }}
                  >
                    Share
                  </Switch>
                ) : (
                  <Text>Shared by {template.owner}</Text>
                )}
              </Flex>
              <Flex width="150px" align="center">
                <Switch
                  isChecked={template.enabled[bid]}
                  onChange={(e) => {
                    updateTemplateField(template, "enabled", e.target.checked);
                  }}
                >
                  Enabled
                </Switch>
              </Flex>
              <Flex width="150px" align="center" justify="flex-end" pr="20px">
                <Switch
                  isChecked={template.auto_send[bid]}
                  onChange={(e) => {
                    updateTemplateField(template, "auto_send", e.target.checked);
                  }}
                >
                  Auto Send
                </Switch>
              </Flex>
              <Flex width="150px" align="center" justify="flex-end" pr="20px">
                  {moment(template.created).format('L')}
              </Flex>
              <Flex ml="auto" align="center">
                <Menu>
                  <MenuButton
                    as={IconButton}
                    aria-label="Options"
                    icon={<FontAwesomeIcon icon={faEllipsisH} />}
                    variant="outline"
                  />
                  <MenuList>
                    <MenuItem>
                      <Link
                        onClick={(e) => {
                          e.preventDefault();
                          downloadFile(template.url, template.name);
                        }}
                        href="#"
                        target="_blank"
                        key={template.url}
                      >
                        Download
                        <ExternalLinkIcon ml="2" />
                      </Link>
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleFileButtonClick(template)}
                    >
                      Update
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setCurrentTemplate(template);
                        setIsModalOpen(true);
                      }}
                    >
                      Ask Patients to Sign
                    </MenuItem>
                  </MenuList>
                </Menu>
              </Flex>
            </Flex>
          </Box>
        ))}
      </Flex>
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalOverlay />
        <ModalContent maxW="50vw" width="50vw">
          <ModalHeader>Send Signature Requests</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>
              You are about to send a signature request to patients who have not received a request to sign the latest version of this form. 
            </Text>
            <Select
              placeholder="Select time frame"
              value={sendOption}
              onChange={(e) => setSendOption(e.target.value)}
              mb={4}
            >
              <option value="12">Only patients who have been to the office in the past 12 months</option>
              <option value="18">Only patients who have been to the office in the past 18 months</option>
              <option value="24">Only patients who have been to the office in the past 24 months</option>
            </Select>
            <Switch
              isChecked={excludeSigned}
              onChange={(e) => setExcludeSigned(e.target.checked)}
              mb={4}
            >
              Exclude patients who have signed an older version of this form
            </Switch>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSend}>
              Send
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Templates;
